// The envelope itself — its field shapes and the two
// questions answered off a whole envelope. See `../task-envelope.ts` for the
// file docblock.

import { z } from 'zod';
import { BYOKProviderSchema } from '../../entities/byok';
// The MODULE, not `entities/raw-task`'s barrel, and the exception is demonstrated
// rather than assumed: that barrel re-exports `raw-task/row.ts`, which imports THIS
// directory's barrel, so the round trip is a real cycle — `bun run test` on the
// intake suite dies with `Cannot access 'RepoFullNameSchema' before initialization`,
// the TDZ throw `packages/domain/CLAUDE.md` names. `raw-task/evidence.ts` imports
// zod and nothing else, so reaching it directly cannot close a loop.
import { GitlabProjectPathSchema } from '../../entities/gitlab-forge';
import { RAW_TASK_URL_MAX } from '../../entities/raw-task/evidence';
import { FORGES } from '../forge';
import { AccountIdSchema } from '../ids';
// A leaf: `../model-call` imports only zod and `../ids`.
import { ModelCallTaskSpecSchema } from '../model-call';
import { MaintenanceRecipeSchema } from '../repo-enablement';
import {
  BabysitTaskSpecSchema,
  coversNothing,
  FILES_TOUCHED_MAX,
  FILES_TOUCHED_PATH_MAX,
  SeamChangeSchema,
} from './babysit-spec';

/**
 * A task's declared blast radius: repo-relative FILE paths, or SUBTREE entries ending in
 * `/` (`isSubtreeEntry`) that cover every path beneath them. An entry that normalizes to
 * nothing is refused — it would cover nothing and read as provably disjoint from every
 * sibling (`coversNothing`).
 */
const FilesTouchedSchema = z
  .array(
    z
      .string()
      .min(1)
      .max(FILES_TOUCHED_PATH_MAX)
      .refine((entry) => !coversNothing(entry), {
        message: 'a files_touched entry must name a path — this one normalizes to nothing',
      }),
  )
  .max(FILES_TOUCHED_MAX);

import { GROUNDING_MAX_FACTS, GroundedFactSchema } from './grounding';
import { OperatorNotesSchema } from './operator-note';
import { TaskOriginSchema, TaskSinkSchema } from './origin';
import { ReviewReplyTaskSpecSchema, ReviewTaskSpecSchema } from './review-spec';
import { TaskClass, TaskClassSchema } from './task-class';

/**
 * `owner/repo` — the ONE declaration of a GitHub full name, anchored and
 * length-bounded on BOTH halves to GitHub's own issuing rules: an owner is
 * alphanumeric + hyphen and cannot lead with a hyphen, a repo name additionally
 * allows `.` and `_`, and neither half may exceed 100 characters.
 *
 * The same charset the API's `RepoFullNameParamsSchema`
 * (`apps/api/src/middleware/params.ts`) gives the `/:owner/:repo` PATH segments,
 * so a repo addressable by path and a repo nameable in a body are one alphabet.
 * That claim is checkable rather than asserted:
 * `apps/api/src/routes/me-github-token.test.ts` generates each half's boundary
 * cases and requires this schema and that one to agree on every single one.
 *
 * Anchoring is load-bearing, not tidiness. A full name reaches URL PATHS
 * un-encoded (`packages/github` `probeFetch` interpolates it into
 * `https://api.github.com/repos/${repo}`), where the URL parser RESOLVES dot
 * segments — so a laxer "no whitespace, one slash" rule lets `../..` collapse
 * the request onto an unrelated endpoint. `apps/api`'s `/v1/me/github-token`
 * carried exactly such a private copy until it was deleted in favour of this one.
 */
export const RepoFullNameSchema = z
  .string()
  .regex(/^[A-Za-z0-9][A-Za-z0-9-]{0,99}\/[A-Za-z0-9._-]{1,100}$/, 'expected "owner/repo"');

/**
 * A task's repository path on ITS forge (#7921): GitHub's (and Gitee's) `owner/repo`
 * ({@link RepoFullNameSchema}), or a GitLab `path_with_namespace`, whose namespace may be a
 * subgroup chain (`group/sub/project`). The field admits either; which one an envelope may
 * carry is its forge's, checked at the write seam (`taskRepoPathRefusal`, `./forge.ts`, called by
 * `packages/db` `sealEnvelope`). It is not a refinement on the envelope object because that
 * makes `TaskEnvelopeSchema.pick` throw, and the field-patch writers rely on `.pick`.
 */
export const TaskRepoPathSchema = z.union([RepoFullNameSchema, GitlabProjectPathSchema]);

/**
 * Who pulled the trigger, and — for a `user`-tier token — HOW.
 *
 * `kind` is an auth TIER, never a claim of personhood. `user` means a
 * USER-tier token (REST or MCP) authenticated the request — that is ALL it
 * has ever meant: the box's own installation token arrives as `kind: 'user'`
 * too (`apps/api/src/deps/army-audit-user-id.test.ts:51`, "its own
 * installation token arrives as `{kind:'user', token_id: '...'}`"). `system`
 * means an ingest lane (sentry/linear/gh webhook) authenticated it instead,
 * named so the audit row can say which one.
 *
 * `via` (#4699) is the CHANNEL behind a `user`-tier token, and it is the field
 * that can actually evidence a human. `'session'` is a live dashboard cookie —
 * `apps/api/src/middleware/auth.ts`'s own comment on `Principal.sessionId`
 * reads "every session names a human". `'pat'` is a bearer personal-access
 * token, which an agent and a person authenticate through IDENTICALLY —
 * `principal.userId` resolves the same way for both (`middleware/auth.ts`),
 * and "MCP callers ride this same path" too. Only `via === 'session'`
 * evidences a human; `via === 'pat'`, or `via` ABSENT (a legacy envelope
 * minted before this field existed — channel unknown), does not. See
 * {@link DispatchChannel} and `planInitiatorOf` (`./plan-projection.ts`),
 * which convicts on exactly this distinction instead of on `kind`.
 *
 * `user_id` (the `users.id` behind the token) is stamped server-side alongside
 * `token_id` — the durable "who dispatched this" the runner-ownership model
 * needs: a PRIVATE-to-user box claims only tasks its owner dispatched, and a
 * token can be revoked while the person persists. Optional (additive; a token
 * with no bound user — a legacy PAT — omits it and simply never matches a
 * user-owned box). The db denormalizes it into `tasks.dispatched_by_user_id`
 * for the indexed claim scope. `user_id` names WHO; `via` names WHETHER a
 * person was present — a PAT can carry a bound `user_id` and still be a PAT.
 */
export const DispatchedBySchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('user'),
    token_id: z.string().min(1).max(128),
    user_id: z.string().min(1).max(128).optional(),
    /**
     * The auth channel (#4699) — see the docblock above. Optional because this
     * schema parses envelopes already stored in `tasks.envelope`: a REQUIRED
     * field fails every historical row at read. Absent means "minted before
     * this change, channel unknown" and maps to `system` — never a claimed
     * human, the same rule `PlanInitiator`'s own docblock states for every
     * other kind of missing evidence.
     */
    via: z.enum(['session', 'pat']).optional(),
  }),
  z.object({ kind: z.literal('system'), source: z.string().min(1).max(64) }),
]);
export type DispatchedBy = z.infer<typeof DispatchedBySchema>;

/**
 * The auth channel behind a `kind: 'user'` dispatch (#4699) — `'session'` |
 * `'pat'`. A TYPE ALIAS ONLY, no companion const object: every comparison in
 * this codebase against `DispatchedBy['kind']` is written against the bare
 * string literal (`kind === 'user'`), and `via` follows the same convention
 * rather than inventing a second one. A const-member comparison here would
 * false-FAIL the post-merge live-test fence (which greps the merged tree for
 * the literal `via === 'session'`) and arm `revert.ts` against a healthy
 * merge.
 */
export type DispatchChannel = NonNullable<Extract<DispatchedBy, { kind: 'user' }>['via']>;

/**
 * Per-task LLM preference (multi-provider pool). An org can hold MANY valid
 * BYOK keys at once (e.g. 1 openrouter + 3 z.ai + 5 cerebras); this picks
 * which provider×model a task runs on. All three fields optional — `provider`
 * alone means "any key on that provider"; `key_label` pins one exact pool key
 * by its account-unique handle (the sharpest override); omitted entirely lets
 * tier routing choose, falling back to the account's most-recently-verified key
 * (`findKeyFor` / `resolveLlmFromPool`). Still no BYOK material: this names a
 * key, never carries one. "Explicit override wins" — a named key/provider beats
 * the tier router.
 */
export const TaskLlmPreferenceSchema = z.object({
  provider: BYOKProviderSchema.optional(),
  model: z.string().min(1).max(200).optional(),
  /** Pin one exact pool key by its account-unique `byok_keys.label`. */
  key_label: z.string().min(1).max(64).optional(),
});
export type TaskLlmPreference = z.infer<typeof TaskLlmPreferenceSchema>;

/**
 * ONE pin's identity — the three axes in a FIXED order, an absent axis as `''`.
 *
 * NEVER `JSON.stringify`: that serializes in INSERTION order, so the same pin
 * written `{provider, model}` on one row and `{model, provider}` on another
 * reads as two, and Zod's object parse does not normalise key order. The
 * separator is `\u0000` so an axis cannot swallow another's text.
 *
 * THE ONE SPELLING OF A PIN, shared by the two ends of the triage lane: the
 * PRODUCER groups a claimed batch by it (`apps/worker`
 * `consumers/raw-task-bridge.lane.ts`) and the CONSUMER reads a batch's SOLE
 * distinct pin with it (`apps/agents-manager` `src/triage-key-failover.ts`).
 * Two spellings would let one pin read as two pins, and a person whose model
 * pick was honoured on one row would have it ignored on the next.
 *
 * A pin NAMES a key, a provider and a model; it never carries BYOK material.
 */
export function llmPinKey(pin: TaskLlmPreference): string {
  return [pin.key_label, pin.provider, pin.model].map((v) => v ?? '').join('\u0000');
}

/**
 * Does this pin NAME anything? All three axes of {@link TaskLlmPreferenceSchema} are
 * optional, so `{}` parses — and stored, it is a pin that boosts no key while still
 * reading as "pinned" to every reader that asks whether an `llm` is present. The ONE
 * predicate both doors that store a person's model pick spend: the blocker answer
 * (`blockerAnswerInputOf` drops an empty one) and the plan model (#7012 refuses one).
 */
export function llmPinNamesSomething(pin: TaskLlmPreference): boolean {
  return pin.key_label !== undefined || pin.provider !== undefined || pin.model !== undefined;
}

/**
 * Are two nullable pins one pin? Both null (Automatic) or both spelling the same three axes
 * by {@link llmPinKey} — never `JSON.stringify`, which reads key order. The plan model's
 * no-op test (#7012): setting the model a plan already has writes no audit row.
 */
export function sameLlmPin(a: TaskLlmPreference | null, b: TaskLlmPreference | null): boolean {
  if (a === null || b === null) return a === b;
  return llmPinKey(a) === llmPinKey(b);
}

/**
 * The task `problem` length cap — the ONE definition. Both envelope schemas bound
 * `problem` by it, so a clamp that imports it (or reads the schema's own
 * `maxLength`) can never exceed what the parse admits; the one-impl
 * `task-problem-cap` concern refuses a second literal definition.
 */
export const TASK_PROBLEM_MAX = 10_000;

/** Whose words a task's text is (#7785). The doc is the field's, on {@link TaskEnvelope}. */
export const TextProvenanceSchema = z.object({
  author_standing: z.string().min(1).max(32),
  /**
   * Whose words these are as one class (#7785 part 2), read through `textProvenanceClassOf`. An
   * unknown spelling cannot pass as first party: the enum refuses it at this seam. Absent on
   * every envelope stored before it.
   */
  class: z.enum(['first_party', 'external']).optional(),
  /** The `raw_tasks.source` it came in by. A string, for the reason `author_standing` is. */
  source: z.string().min(1).max(32).optional(),
  raw_task_id: z.string().min(1).max(64).optional(),
  author_login: z.string().min(1).max(100).optional(),
});

export const TaskEnvelopeSchema = z.object({
  origin: TaskOriginSchema,
  account_id: AccountIdSchema,
  repo_full_name: TaskRepoPathSchema,
  /**
   * WHICH FORGE `repo_full_name` is on (#7720), with its INSTANCE host. Optional, and absent
   * is GitHub's `github.com` — every envelope written before this field was GitHub's, so the
   * absence keeps its meaning and no stored envelope needs rewriting. A non-GitHub task
   * states both; `taskForgeOf` (`./forge.ts`) is the one reader, and refuses a non-GitHub
   * forge that names no instance rather than guessing one.
   */
  forge: z.enum(FORGES).optional(),
  instance: z.string().min(1).max(255).optional(),
  title: z.string().min(1).max(200),
  problem: z.string().min(1).max(TASK_PROBLEM_MAX),
  /** Postconditions = success (Devin "Specifications", doc 06). */
  acceptance_criteria: z.array(z.string().min(1).max(500)).max(20),
  /**
   * From codegraph_impact (doc 10). **No longer merely advisory when non-empty.**
   *
   * The #513 area gate admits two box-held tasks on one repo when BOTH declare a
   * non-empty set and the two do not overlap (`claimNextReady`), and both coding
   * briefs now tell the agent the set is exclusive and to stop-and-report rather
   * than edit outside it. So a declared set is a concurrency contract: writing it
   * loosely no longer costs a slightly-wrong hint, it lets two agents run
   * concurrently on files that actually collide.
   *
   * EMPTY still means "unknown blast radius", which is the safe reading — the gate
   * refuses to prove disjointness against an unscoped task and serialises the repo
   * behind it exactly as before. Absence of proof is not proof of absence.
   */
  files_touched: FilesTouchedSchema,
  /**
   * The CROSS-RUN seam declaration (`hive.md` rule 4's missing half) — see
   * {@link SeamChangeSchema}. Read by the #513 area gate's proof-of-disjointness
   * escape (`provablyDisjointSql`, `@developerz/db`) as extra evidence that a
   * sibling task declaring one of these `readers` in ITS `files_touched` is not
   * safe to run alongside this one, even when the two `files_touched` sets never
   * overlap. Optional, and absent is a clean skip — the pre-existing file-set-only
   * proof, unchanged.
   */
  seam_changes: z.array(SeamChangeSchema).max(FILES_TOUCHED_MAX).optional(),
  /** Ban-wrong-fixes list; maps to policy `forbidden_paths` semantics. */
  forbidden: z.array(z.string().min(1).max(300)).max(50),
  /** 0..1 scoped-plan confidence; gated against policy `confidence_threshold`. */
  confidence: z.number().min(0).max(1),
  sink: TaskSinkSchema,
  dispatched_by: DispatchedBySchema,
  /**
   * WHO wrote this task's own text, where the platform knows (#7785), stamped at the mint and read
   * by the box's run-start screen (`envelopeTextIsFirstParty`, `./../intake-screen.ts`). OPTIONAL:
   * every envelope already stored has none, and none falls back to `dispatched_by`. Today
   * `author_standing` is `developer` (triage minted it from a batch of developers' and the
   * platform's asks), `external` (Send work: a person sent it, but the text is someone else's, so
   * it outranks that session) or `merged` (another task's text was merged in). Only `developer`
   * skips the screen. A STRING, not an enum, so a value a later release adds decodes on an older
   * box and screens there, instead of failing the assignment.
   */
  text_provenance: TextProvenanceSchema.optional(),
  /**
   * Scheduling priority class (slice 06a). Optional on the wire; absent ⇒ `coding`
   * via {@link taskClassOf}. Orders claim + idle-box arbitration, never model choice.
   */
  class: TaskClassSchema.optional(),
  /**
   * Capability tokens the running box MUST advertise (slice 06a, #510 read-half) —
   * matched against a box's `runners.capabilities.provisions` by `filterByCapability`
   * / `capabilitiesSatisfy`. Same token vocabulary as `RunnerCapability` (`browsers`,
   * `docker`, `toolchain:<stack>`, `ci`, …). Empty / absent ⇒ the task runs on ANY
   * box (never stranded); a task whose tokens no online box carries is ESCALATED, not
   * spun forever. Advisory to placement only — not an allowlist of what it may touch.
   */
  requires: z.array(z.string().min(1).max(64)).max(20).optional(),
  /** Optional provider×model pick from the account's BYOK pool. */
  llm: TaskLlmPreferenceSchema.optional(),
  /** Present only for `origin: 'pr_review'` tasks (slice 06) — the PR under review. */
  review: ReviewTaskSpecSchema.optional(),
  /** Present only for `origin: 'pr_review_reply'` tasks (#258) — the PR whose threads to engage. */
  review_reply: ReviewReplyTaskSpecSchema.optional(),
  /** Present only for `origin: 'pr_babysit'` tasks (#543) — the PR + action to drive toward merge. */
  babysit: BabysitTaskSpecSchema.optional(),
  /**
   * Present only for `origin: 'triage_judge'` / `'kb_fold'` tasks (#6759) — the
   * `model_call_jobs` row whose prompt the machine fetches. Optional like its siblings:
   * nothing mints either origin in this release.
   */
  model_call: ModelCallTaskSpecSchema.optional(),
  /**
   * The GitHub ISSUE this task answers, when it has one (#366) — the durable
   * task↔issue link, and the thread the lifecycle narration comments on before a
   * PR exists.
   *
   * It has to be its own field. Every other link on the envelope is PR-shaped
   * (`review` / `review_reply` / `babysit`) and `tasks.pull_request_number` is
   * only written once a PR OPENS — so between "picked up" and "PR opened", the
   * two moments a reporter most wants narrated, nothing pointed back at the
   * issue. The one prior trace was a `Source: <html_url>` line appended into
   * `problem` (`normalizeIntake`), which is prose for the model to read, not a
   * link a consumer may parse.
   *
   * Optional, and absent is a clean skip: a task with no issue simply narrates on
   * its PR once one exists, and one with neither is never narrated. Set by the
   * `gh_issue` intake mapper and settable by a `POST /v1/tasks` caller
   * dispatching against a known issue.
   */
  issue_number: z.number().int().positive().optional(),
  /**
   * The PROVIDER's own identifier for the thing this raw signal is about — a
   * Sentry issue, a Linear ticket. Stable across every delivery reporting the same
   * thing, which is the entire point: it is the idempotency key a duplicate has to
   * collide on.
   *
   * The dedup gate that was doing this job alone cannot. It ranks IN-MOTION tasks
   * by embedding similarity, which is a JUDGEMENT made from a read — and two
   * deliveries of one Sentry issue arriving inside the read-to-write window both
   * see an empty board and both schedule. `crypto.randomUUID()` ids mean nothing
   * downstream collides either. At `AGENTS_MANAGER_CONCURRENCY` 25, that window is
   * wide open for any lane that does not serialize per tenant.
   *
   * Identity closes it where a score cannot: `rawSignalTaskId` derives the row's
   * primary key from this, so the second insert is a no-op in Postgres rather than
   * a second task, a second box and a second pull request for one error.
   *
   * OPTIONAL, and absent means exactly the old behaviour — a random id and the
   * similarity gate alone. A provider that sends nothing stable is not a reason to
   * refuse the signal.
   */
  source_ref: z.string().min(1).max(200).optional(),
  /**
   * The provider's PERMALINK for that same thing (#3453) — the Sentry issue's
   * `web_url`, the Linear ticket's `url`, the GitHub issue's `html_url`.
   *
   * A SECOND field rather than a widening of `source_ref`, because the two answer
   * different questions and only one of them may change shape. `source_ref` is the
   * idempotency key — `rawSignalTaskId` derives a primary key from it — so it has to
   * be the most stable id the provider has, which for Sentry is the short id
   * (`JAVASCRIPT-2F`) and not a url. Carrying the url there would either break the
   * key or lose the link.
   *
   * It exists because the link was reaching the raw task only inside `problem`
   * prose: `evidence.urls` is built from `source_ref` filtered to `isHttpUrl`, so a
   * source with a real short id got an EMPTY evidence list while its permalink sat
   * in the signed payload. Measured on prod 2026-09-08: 313 of 333 raw tasks are
   * `gh_issue`, and that normalizer passed no ref at all.
   *
   * OPTIONAL and FILTERED, never refused: a source that sends no link, or sends
   * something that is not one, still files its ask. `isHttpUrl` is the one
   * definition of a link on both sides of this seam.
   */
  source_url: z.string().min(1).max(RAW_TASK_URL_MAX).optional(),
  /**
   * Hold the plan's authored tasks for approval before they are queued (#2680) —
   * a plan-group state flag, NOT a per-task hold, because holding a task that
   * exists but cannot be claimed is a strictly worse object than one that has
   * not been minted yet.
   *
   * Defaults OFF. Carried on the anchor scout task's envelope and read at ingest
   * (`apps/worker/src/fleet/scout-plan-ingest.ts`); when set, the scout-plan
   * ingest inserts the plan's groups in `PlanGroupStatus.Held` and mints NO
   * tasks — the `plan approve` action flips them and mints the tasks. `claimNextReady`
   * never sees a held group, which is the point.
   *
   * NOT a refusal, NOT an escalation: the scoping here (the MCP conversation
   * lane) is the customer's own work on their own repo, and they already have
   * unconditional dispatch authority over it today. Different gate, different
   * party than `ops.ts`'s caller-supplied approval — that one gates the platform's
   * own ops actions against a self-attesting caller.
   */
  require_approval: z.boolean().optional(),
  /**
   * Answer this scout run with the CONTEXT it found instead of a plan (#3358).
   *
   * Read BY THE BOX, which is the one difference from its neighbour above:
   * `require_approval` is read at ingest, this is read at `modes/scout/executor.ts`
   * before the plan is authored. Defaults OFF, absent rather than `false`, so every
   * envelope minted before this field existed reads exactly as it did.
   *
   * When set, the scout surveys the repo exactly as it does today and then emits
   * its findings as a `scout_context` status event — no `plan_authored`, so
   * `detectAuthoredPlan` (`apps/worker/src/fleet/event-sink.ts`) matches nothing,
   * `ingestScoutPlanTasks` never runs, and neither an authored child task nor a
   * `plan_groups` row is minted. The ANCHOR scout task carrying this field still
   * exists — `plan_start` minted it to run the survey, and it is the address the
   * answer arrives on. The caller reads the payload off that run's tail and writes
   * the ask itself.
   *
   * NOT the scout's cutover away from minting — that is #2325, and it deletes the
   * other arm rather than switching between them. This is a second ANSWER SHAPE
   * the caller picks per call: both arms are reachable and exercised, and the
   * minting arm is byte-for-byte unchanged when the field is absent.
   */
  return_context: z.boolean().optional(),
  /**
   * The FAN-OUT this scout task belongs to (#3359) — one id shared by every
   * sub-scout minted from one multi-repo ask, and the address the caller polls.
   *
   * Read by NEITHER the box nor the ingest: it is how the mothership finds the
   * siblings of a sub-scout at read time, so their `scout_context` answers can be
   * merged into one (`mergeScoutContexts`, `wire/scout-context.ts`). Each sub-scout
   * still surveys exactly one repo and is a complete scout task on its own — the
   * field adds a grouping, never a dependency between them, so one repo answering
   * late leaves the others' answers readable rather than pending behind it.
   *
   * ABSENT on a single-repo ask, which is the whole of today's traffic: a caller
   * that named one repo gets the task id it has always got and no fan-out exists
   * to read. Set only when `repo` carried more than one name.
   */
  fanout_id: z.string().min(1).max(64).optional(),
  /**
   * The CONVERSATION that asked for this task (#1896) — an opaque identifier
   * minted by the asking product (tesote.ai), carried to the PR body so a change
   * on GitHub traces back to the thread that asked for it.
   *
   * CALLER-SUPPLIED, never server-derived, and that is the whole contract. The
   * server has no source for a conversation id and must not invent one; in
   * particular `McpCaller.sessionId` is a DIFFERENT identifier with a different
   * lifecycle, and substituting it would mint a plausible wrong answer that
   * nobody could later distinguish from a real one.
   *
   * Bounded and charset-fenced like every other envelope string, because this one
   * renders into a PR body on repos we do not own: 1..200 chars of
   * `[A-Za-z0-9_-]`, so it cannot carry markdown, a URL, whitespace, or a
   * sentence of someone's prose.
   *
   * Optional, and absent is a clean skip — the provenance block simply omits the
   * line. Persisted BOTH in this jsonb envelope and in the dedicated
   * `tasks.conversation_ref` column, which is the queryable surface;
   * `createTask` derives the column from this field so the two cannot disagree.
   */
  conversation_ref: z
    .string()
    .min(1)
    .max(200)
    .regex(/^[A-Za-z0-9_-]+$/, 'conversation_ref must be 1-200 chars of [A-Za-z0-9_-]')
    .optional(),
  /**
   * The stack the MAINTAINER asked for, verbatim (arc 101 doc 10 step 6) — the
   * greenfield `project create` / `project setup` ask, carried to the box that
   * scaffolds the repo.
   *
   * It is an ASK, never a detection. `detectStack`
   * (`apps/runner/src/modes/setup/base/stack.ts`) answers from markers in the checkout
   * and is the authority everywhere else; an EMPTY repo has no markers, so the
   * one thing that can say "this is an Ultimate project" is the person who asked
   * for it. Which is also why it must not be silently substituted: the box either
   * honours the named generator or refuses it by name.
   *
   * Free text, same 1..40 bound as the `project create` input it carries
   * (`packages/mcp/src/army/project.ts`), NOT an enum: the vocabulary of
   * generators lives on the runner, packages may not import apps, and a domain
   * enum here would be a second roster to keep in step with that one. The runner
   * normalizes (trim + lowercase) and matches; anything it does not recognize
   * stays exactly as advisory as it is today.
   *
   * Optional, and absent is a clean skip — the scaffold auto-detects, which is
   * every pre-#2683 caller's behaviour unchanged.
   */
  stack: z.string().min(1).max(40).optional(),
  /**
   * The starting policy a person chose when they enabled this repo (#7802): which document
   * the setup pass proposes as `.dz/maintainer/maintainer.yml`
   * (`apps/runner/src/modes/setup/base/maintainer-recipes.ts`). It chooses a PROPOSAL inside a
   * pull request a person merges, and never overrides a policy file the repo already has.
   * Absent is the house posture, which is every other producer's pass.
   *
   * THE BOX MUST KNOW THE FIELD, so a producer sets it together with `requires:
   * [SETUP_RECIPE_CAPABILITY]`: a build older than this field strips it and would propose
   * `auto_merge: true` for a person who chose `review_only`.
   */
  maintenance_recipe: MaintenanceRecipeSchema.optional(),
  /**
   * WHAT THE SCOUT ALREADY READ, carried to the box that will do the work — one
   * anchored fact per entry (see {@link GroundedFactSchema}).
   *
   * The scout is the only box that had the repo open. It clones, surveys it with
   * read-only scouts, probes their `path:line` citations against the real checkout
   * when it has one (`verifySurveyAnchors` — see the last paragraph for the box that
   * does not), adjudicates the model's declared blast radius
   * against those anchors ({@link provableFilesTouched} via `provenFileSet`) — and
   * then dropped everything except the paths that survived that adjudication. The
   * coding box claims the task hours later, on a different machine, and starts from
   * the prose.
   *
   * Measured on prod 2026-09-01: of 829 scout-authored tasks, **594 (72%) name no
   * repository path at all** in `problem` and only 114 (14%) carry a `path:line`,
   * while `scout_file_set` records 1,624 verified anchors handed to the brain across
   * 83 plans. The evidence existed, was checked against a real checkout, and was
   * thrown away one hop before the agent that needed it.
   *
   * It is EVIDENCE, never a permission and never a lock. `files_touched` is what
   * fences writing; this is a reading list, and every consumer must present it as
   * the scout's report rather than as established truth — the anchor was probed,
   * the CLAIM never was.
   *
   * Optional, and absent is a clean skip: every non-scout lane mints exactly the
   * envelope it minted before, and a scout whose survey never ran (or ran on a box
   * that could not stat its own checkout) carries nothing rather than carrying
   * anchors nobody could check.
   */
  grounding: z.array(GroundedFactSchema).max(GROUNDING_MAX_FACTS).optional(),
  /**
   * What an OPERATOR told this task between runs (`./operator-note.ts`): the
   * `guidance` of a `task redispatch`, oldest first. SERVER-STAMPED —
   * the redispatch control is its only producer — and rendered into the next run's
   * brief as its own section, never folded into `acceptance_criteria` (a note is
   * what went wrong last time, not a postcondition of the ask).
   *
   * Optional, and absent means no operator has spoken: every envelope minted before
   * this field existed reads exactly as it did.
   */
  operator_notes: OperatorNotesSchema.optional(),
  /**
   * The role a person SENT this task to on one machine (#6767, `POST
   * /v1/runners/:id/work`), stamped only for Escalation: the task escalates from its first
   * attempt (`ModelEscalationTrigger` `sent`). SERVER-STAMPED by that route only.
   *
   * Optional, and absent means every envelope reads as it did.
   */
  sent: z.object({ role: z.literal('escalation') }).optional(),
  /**
   * The plan that MINTED this row, on a setup pass's one-time ask only (#7169): the
   * `plan_authored` run id whose ingest inserted it, written in the SAME insert, so it
   * is atomic with the row. SERVER-STAMPED (`apps/worker`
   * `fleet/scout-plan-ingest.ts`); the wire's own value never reaches here, because the
   * ingest rebuilds every child envelope. The ask's id is permanent, so a later pass's
   * filing conflicts on it; this is how that conflict tells the ORIGINATING plan
   * (allowed to restore a group link its first delivery lost) from any other (never).
   *
   * Optional: absent on every other task, and on asks minted before this field, which
   * keep the earlier rule (`apps/worker` `fleet/setup-ask-conflict.ts`).
   */
  setup_ask_plan_id: z.string().min(1).max(200).optional(),
});
export type TaskEnvelope = z.infer<typeof TaskEnvelopeSchema>;

/**
 * An envelope's scheduling {@link TaskClass}. The ONE place the `coding` default
 * lives: an envelope with no explicit `class` (every pre-06a producer, and any
 * producer a later slice hasn't taught to stamp one) schedules as `coding`. Pure —
 * reads only the envelope. Distinct from `jobClassForEnvelope` (`job-class-routing.ts`,
 * model routing): a task has both a scheduling class and a job class.
 */
export function taskClassOf(envelope: TaskEnvelope): TaskClass {
  return envelope.class ?? TaskClass.Coding;
}

/**
 * Does a run of this assignment need a GitHub credential — i.e. does it TOUCH A
 * REMOTE at all?
 *
 * WHY THE QUESTION EXISTS. The box asks the mothership to mint a per-claim
 * installation token for `envelope.repo_full_name` before it does anything that
 * reads GitHub, and a terminal mint refusal (`unknown_repo`) correctly ends the
 * run. `class: 'gardener'` is the one assignment that has a `repo_full_name` and
 * NEVER touches it: `GARDENER_MAINTENANCE_REPO`
 * (`apps/worker/src/cron/gardener.ts`) is a SENTINEL — a name the dispatch
 * contract requires and no repository behind it — and the run maintains the box
 * that executes it. The mothership answered `unknown_repo` because that is the
 * truth, the box refused because a terminal refusal is terminal, and the weekly
 * fleet-upkeep lane failed 31 of 31 tasks across three weeks (#3351). Neither
 * half was wrong; nothing was asking whether the credential was needed.
 *
 * A `switch` with no `default`, so a FIFTH {@link TaskClass} fails typecheck here
 * until someone decides whether it touches a remote — the same fail-fast-on-
 * unknown shape {@link TASK_FAILURE_BUDGET}'s `Record` gives failure kinds.
 *
 * `gardener` IS box maintenance, exhaustively, and that is measured rather than
 * assumed: `apps/worker/src/cron/gardener.ts:246` is the only expression in the
 * repo that stamps the class (`grep -rn 'TaskClass.Gardener' apps packages`), and
 * all 46 gardener-class rows on prod carry `repo_full_name =
 * developerz-ai/box-maintenance` and no other value (2026-09-07). If a second
 * producer ever stamps `gardener` on real repository work, this predicate is the
 * line it has to change.
 */
export function assignmentNeedsGitCredential(envelope: TaskEnvelope): boolean {
  switch (taskClassOf(envelope)) {
    case TaskClass.Gardener:
      return false;
    case TaskClass.Interactive:
    case TaskClass.Coding:
    case TaskClass.Ci:
      return true;
  }
}
